import React from 'react';
import { Progress, Space, Tag, Typography } from 'antd';
import type { EnrichedExecutionCargo } from '../routes/execution/cargoHumanization';
import { formatCargoSubtitle } from '../routes/execution/cargoHumanization';
import {
  getExecutionStatusBadgeProps,
  normalizeCargoOperationalState
} from '../routes/execution/statusFacade';

const { Text } = Typography;

type Props = {
  cargo: EnrichedExecutionCargo;
  fromLabel?: string;
  toLabel?: string;
  compact?: boolean;
};

export const PartialExecutionCargoRow: React.FC<Props> = ({
  cargo,
  fromLabel,
  toLabel,
  compact = false
}) => {
  const operationalState = normalizeCargoOperationalState(cargo.executionState);
  const badge = getExecutionStatusBadgeProps(operationalState);
  const loadPct = cargo.plannedQty > 0 ? Math.round((cargo.loadedQty / cargo.plannedQty) * 100) : 0;
  const unloadPct = cargo.plannedQty > 0 ? Math.round((cargo.unloadedQty / cargo.plannedQty) * 100) : 0;
  const { human } = cargo;

  return (
    <div
      style={{
        padding: compact ? 8 : 12,
        border: '1px solid #f0f0f0',
        borderRadius: 6,
        marginBottom: 8
      }}
    >
      <Space direction="vertical" size={2} style={{ width: '100%', marginBottom: 6 }}>
        <Space wrap>
          <Text strong>{human.name}</Text>
          <Tag color={badge.color}>{badge.label}</Tag>
          {cargo.isPartiallyDelivered ? <Tag color="warning">Частичная доставка</Tag> : null}
          {cargo.isDelivered ? <Tag color="success">Доставлен</Tag> : null}
        </Space>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {formatCargoSubtitle(human, cargo.plannedQty)}
        </Text>
        {human.applicationNumber ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Заявка №{human.applicationNumber}
            {human.clientName ? ` · ${human.clientName}` : ''}
          </Text>
        ) : human.clientName ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {human.clientName}
          </Text>
        ) : null}
        <Text type="secondary" style={{ fontSize: 11 }}>
          ID {cargo.cargoId}
        </Text>
      </Space>

      {fromLabel || toLabel ? (
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
          {fromLabel ?? 'Погрузка'} → {toLabel ?? 'Выгрузка'}
        </Text>
      ) : null}

      <Space direction="vertical" style={{ width: '100%' }} size={4}>
        <div>
          <Text style={{ fontSize: 12 }}>План: {cargo.plannedQty}</Text>
          <Text style={{ fontSize: 12, marginLeft: 12 }}>Погружено: {cargo.loadedQty}</Text>
          <Text style={{ fontSize: 12, marginLeft: 12 }}>Выгружено: {cargo.unloadedQty}</Text>
          <Text style={{ fontSize: 12, marginLeft: 12 }}>Остаток: {cargo.remainingQty}</Text>
        </div>
        {!compact ? (
          <>
            <Progress percent={loadPct} size="small" format={() => `Погрузка ${cargo.loadedQty}/${cargo.plannedQty}`} />
            <Progress
              percent={unloadPct}
              size="small"
              status={cargo.isPartiallyDelivered ? 'active' : 'normal'}
              format={() => `Выгрузка ${cargo.unloadedQty}/${cargo.plannedQty}`}
            />
          </>
        ) : null}
      </Space>
    </div>
  );
};

export default PartialExecutionCargoRow;
